type ReferenceLike = {
  doc_id?: string | null;
  link?: string | null;
  pmcid?: string | null;
  search_gateway?: string | null;
  title?: string | null;
  full_text_is_free?: boolean | null;
  full_text_resources?: FullTextResourceLike[] | null;
};

type FullTextResourceLike = {
  mime_type?: string | null;
  link?: string | null;
  url?: string | null;
};

export type FullTextActionState = {
  originalUrl: string;
  showFullTextPdf: boolean;
  showTranslateFullText: boolean;
  translateHref: string;
};

export type ReferencePreviewFullTextActionState = FullTextActionState & {
  showActions: boolean;
  showSource: boolean;
  sourceUrl: string;
};

function isPubMedReference(reference: ReferenceLike): boolean {
  return (
    reference.search_gateway?.toLowerCase() === 'pubmed' ||
    reference.doc_id?.toLowerCase().startsWith('pubmed|') === true
  );
}

function isOpenAlexReference(reference: ReferenceLike): boolean {
  return (
    reference.search_gateway?.toLowerCase() === 'openalex' ||
    reference.doc_id?.toLowerCase().startsWith('openalex|') === true ||
    reference.link?.toLowerCase().includes('openalex.org/') === true
  );
}

function normalizePmcid(pmcid?: string | null): string {
  return pmcid?.trim().replace(/^\/+|\/+$/g, '') ?? '';
}

export function getPmcArticleUrl(reference: ReferenceLike): string {
  const pmcid = normalizePmcid(reference.pmcid);
  if (!pmcid || !isPubMedReference(reference)) return '';
  return `https://pmc.ncbi.nlm.nih.gov/articles/${pmcid}/`;
}

function getResourceUrl(resource: FullTextResourceLike | null | undefined) {
  return resource?.link?.trim() || resource?.url?.trim() || '';
}

function isPdfResource(resource: FullTextResourceLike): boolean {
  const normalizedUrl = getResourceUrl(resource)
    .toLowerCase()
    .replace(/[?#].*$/, '');

  return (
    resource.mime_type?.toLowerCase().includes('pdf') === true ||
    normalizedUrl.endsWith('.pdf') ||
    normalizedUrl.endsWith('/pdf')
  );
}

function isHtmlResource(resource: FullTextResourceLike): boolean {
  return resource.mime_type?.toLowerCase().includes('html') === true;
}

function getFullTextResources(
  reference: ReferenceLike,
): FullTextResourceLike[] {
  return Array.isArray(reference.full_text_resources)
    ? reference.full_text_resources
    : [];
}

function hasFullTextResources(reference: ReferenceLike): boolean {
  return getFullTextResources(reference).length > 0;
}

export function getFullTextPdfUrl(reference: ReferenceLike): string {
  return (
    getResourceUrl(getFullTextResources(reference).find(isPdfResource)) || ''
  );
}

export function getTranslatableFullTextResourceUrl(
  reference: ReferenceLike,
): string {
  if (reference.full_text_is_free !== true) return '';

  const resources = getFullTextResources(reference);
  return (
    getResourceUrl(resources.find(isPdfResource)) ||
    getResourceUrl(resources.find(isHtmlResource)) ||
    ''
  );
}

export function getOriginalArticleUrl(reference: ReferenceLike): string {
  const fullTextPdfUrl = getFullTextPdfUrl(reference);
  if (fullTextPdfUrl) return fullTextPdfUrl;
  if (isOpenAlexReference(reference)) return reference.link ?? '';
  return getPmcArticleUrl(reference);
}

export function getTranslatableArticleUrl(reference: ReferenceLike): string {
  const fullTextResourceUrl = getTranslatableFullTextResourceUrl(reference);
  if (fullTextResourceUrl) return fullTextResourceUrl;

  if (
    reference.full_text_is_free === false ||
    (hasFullTextResources(reference) && reference.full_text_is_free !== true)
  ) {
    return '';
  }

  return getPmcArticleUrl(reference);
}

export function getTranslateFullTextHref(reference: ReferenceLike): string {
  const fullTextPdfUrl = getFullTextPdfUrl(reference);
  if (reference.full_text_is_free === true && fullTextPdfUrl) {
    return `/translate/upload?fileUrl=${encodeURIComponent(fullTextPdfUrl)}`;
  }

  const targetUrl = getTranslatableArticleUrl(reference);
  if (!targetUrl) return '';

  return `/translate/reference?url=${encodeURIComponent(targetUrl)}&referer=pubmed&title=${encodeURIComponent(reference.title ?? '')}`;
}

export function canViewOriginalArticle(reference: ReferenceLike): boolean {
  return Boolean(getOriginalArticleUrl(reference));
}

export function canViewOriginalArticleInList(
  reference: ReferenceLike,
): boolean {
  return Boolean(getFullTextPdfUrl(reference));
}

export function canTranslateOriginalArticle(reference: ReferenceLike): boolean {
  return Boolean(getTranslatableArticleUrl(reference));
}

export function getFullTextActionState(
  reference: ReferenceLike,
): FullTextActionState {
  return {
    originalUrl: getOriginalArticleUrl(reference),
    showFullTextPdf: canViewOriginalArticleInList(reference),
    showTranslateFullText: canTranslateOriginalArticle(reference),
    translateHref: getTranslateFullTextHref(reference),
  };
}

export function getReferencePreviewFullTextActionState(
  reference: ReferenceLike,
): ReferencePreviewFullTextActionState {
  const sourceUrl = reference.link?.trim() ?? '';
  const fullTextActionState = getFullTextActionState(reference);

  return {
    ...fullTextActionState,
    showActions:
      Boolean(sourceUrl) ||
      fullTextActionState.showFullTextPdf ||
      fullTextActionState.showTranslateFullText,
    showSource: Boolean(sourceUrl),
    sourceUrl,
  };
}
